import { Component, computed, inject, OnInit, signal } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatDialogModule } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle'
import { MatTooltipModule } from '@angular/material/tooltip'
import { DEV_OR_LOCALHOST } from '@bases/base.token'
import { HttpService } from '@bases/http.service'
import { ChartComponent } from '@components/chart/chart.component'
import { API_URL } from '@core/constants/constant.data'
import { asyncError, asyncLoading, AsyncState, asyncSuccess } from '@core/models/async-state.model'
import { SpaceUsedResponse } from '@core/models/user-management.model'
import { SessionManagerService } from '@core/services/session-manager.service'

import { buildDate, commitHash, version } from '@env/version'
import { ThemeService } from '@pages/map/theme.service'
import { ChartConfiguration } from 'chart.js'
import Keycloak from 'keycloak-js'
import { BehaviorSubject } from 'rxjs'

@Component({
  selector: 'app-dialog-user-management',
  templateUrl: './dialog-user-management.component.html',
  styleUrl: './dialog-user-management.component.scss',
  providers: [HttpService],
  imports: [
    // Components
    ChartComponent,

    // Material
    MatSlideToggleModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
  ],
})
export class DialogUserManagementComponent implements OnInit {
  devOrLocalhost = inject(DEV_OR_LOCALHOST) as BehaviorSubject<string>
  readonly keycloak = inject(Keycloak)
  readonly themeService = inject(ThemeService)
  readonly sessionManagerService = inject(SessionManagerService)

  protected readonly _httpService = inject(HttpService)

  email = this.sessionManagerService.userInfo$()?.email
  name = this.sessionManagerService.userInfo$()?.name

  versionInfo = `${version} - ${commitHash} (${buildDate})`

  dataSpaceUsedState$ = signal<AsyncState<SpaceUsedResponse>>(asyncLoading())
  dataSpaceUsed$ = computed(() => {
    const state = this.dataSpaceUsedState$()
    return state.status === 'success' ? state.data : { used_size: 0, max_size: 0 }
  })

  userDiskSpaceChart$ = computed<ChartConfiguration<'bar'>>(() => {
    const diskSpaceUsed = this.dataSpaceUsed$().used_size
    const diskSpaceMax = this.dataSpaceUsed$().max_size

    const usagePercentage = (diskSpaceUsed / diskSpaceMax) * 100
    const usedMB = (diskSpaceUsed / (1024 * 1024)).toFixed(2)
    const maxMB = diskSpaceMax / (1024 * 1024)

    return {
      type: 'bar',
      data: {
        labels: [''],
        datasets: [
          {
            label: `User disk space (${usedMB}/${maxMB} MB)`,
            backgroundColor: this._getColorsBasedOnDiskUsage(usagePercentage),
            data: [parseFloat(usagePercentage.toFixed(2))],
          },
        ],
      },
      options: {
        maintainAspectRatio: false,
        indexAxis: 'y', // Horizontal bar chart
        scales: {
          x: { max: 100 }, // Define max to 100%
        },
        plugins: {
          tooltip: {
            callbacks: {
              label: (context) => `${context.dataset.data[context.dataIndex]?.toString()}%`,
            },
          },
        },
      },
    }
  })

  async ngOnInit() {
    this._initSpaceUsed()
  }

  private async _initSpaceUsed() {
    this.dataSpaceUsedState$.set(asyncLoading())
    const response = await this._httpService.GET(API_URL + '/users/space-used')

    if (response === null) {
      this.dataSpaceUsedState$.set(asyncError())
      return
    }
    this.dataSpaceUsedState$.set(asyncSuccess(response))
  }

  // Method to handle logout
  logout() {
    this.sessionManagerService.logout()
  }

  editAccount() {
    this.sessionManagerService.editAccount()
  }

  changeTheme(event: MatSlideToggleChange) {
    if (event.checked) {
      this.themeService.setTheme('dark')
    } else {
      this.themeService.setTheme('light')
    }
  }

  private _getColorsBasedOnDiskUsage(diskSpaceUsedPercentage: number): string {
    if (diskSpaceUsedPercentage < 50) {
      return '#50C878'
    } else if (diskSpaceUsedPercentage < 100) {
      return '#F28C28'
    }
    return '#D22B2B'
  }
}
